import { IrisDeeplLanguage } from '@iris/common/models/IrisDeeplLanguage';
import { FileUploadState, IrisDMSFileI, IrisDMSUploadingFile } from '../models/IrisDMSFile';
import { IrisContentViewType } from '../models/IrisDmsViewType';
import { ImportingFolderTreeStatus } from '../models/ImportingFolderTree';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { differenceBy, isBoolean } from 'lodash';
import { computed, inject } from '@angular/core';
import { IrisDeeplTranslationService } from '@iris/common/services/deepl-translation.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { IrisEnvironmentService } from '@iris/common/services/environment.service';
import { tapResponse } from '@ngrx/operators';
import { IrisDmsSidebarStore } from './dms-sidebar.signal-store';
import { IrisSecurityService } from '../../security/services/security.service';
import { IrisInfrastructureService } from '@iris/common/services/infrastructure.service';
import { IrisDmsFolderViewTab } from '../utils/dms-routing.utils';

export interface IrisDmsGlobalStateI {
  selectedFolderId: string;
  selectedFolderIdModal: string;
  currentProjectIdModal: number;
  selectedFoldersIdsModal: string[];
  contentViewType: IrisContentViewType;
  openedMenuFolderId: string;
  openedMenuFileId: string;
  uploadingFiles: IrisDMSUploadingFile[];
  uploadMinimizedModalShown: boolean;
  deeplLanguages: IrisDeeplLanguage[];
  importingFolderTrees: ImportingFolderTreeStatus[];
  checkAll: boolean;
  folderViewSelectedTab: IrisDmsFolderViewTab;
  showFoldersTree: boolean;
}

const initialState: IrisDmsGlobalStateI = {
  selectedFolderId: null,
  selectedFolderIdModal: null,
  currentProjectIdModal: null,
  selectedFoldersIdsModal: [],
  contentViewType: IrisContentViewType.List,
  openedMenuFolderId: null,
  openedMenuFileId: null,
  uploadingFiles: [],
  uploadMinimizedModalShown: false,
  deeplLanguages: [],
  importingFolderTrees: [],
  checkAll: false,
  folderViewSelectedTab: IrisDmsFolderViewTab.Documents,
  showFoldersTree: true,
};

export const IrisDmsGlobalStore = signalStore(
  { providedIn: 'root' },
  withState<IrisDmsGlobalStateI>(initialState),
  withComputed((
    _store,
    securityService = inject(IrisSecurityService),
  ) => ({
    isAdmin: computed(() => securityService.isAdmin),
  })),
  withComputed(store => ({
    trashBinTabSelected: computed(() => store.folderViewSelectedTab() === IrisDmsFolderViewTab.Trash),
    tagsTabSelected: computed(() => store.folderViewSelectedTab() === IrisDmsFolderViewTab.Tags),
    foldersTabSelected : computed(() => store.folderViewSelectedTab() === IrisDmsFolderViewTab.Documents),
    systemFoldersTabSelected: computed(() => store.folderViewSelectedTab() === IrisDmsFolderViewTab.SystemFolders),
  })),
  withMethods(store => ({
    folderSelected: (selectedFolderId: string) => patchState(store, { selectedFolderId, checkAll: false }),
    setSelectedModalFolderId: (selectedFolderIdModal: string) => patchState(store, { selectedFolderIdModal }),
    setSelectedModalProjectId: (currentProjectIdModal: number) => patchState(store, { currentProjectIdModal }),
    toggleFolderInSelectedListModal: (folderId: string) => {
      const doesFolderExist = store.selectedFoldersIdsModal().some((currentFolderId) => currentFolderId === folderId);
      const selectedFoldersIdsModal = doesFolderExist
        ? store.selectedFoldersIdsModal().filter((currentFolderId) => currentFolderId !== folderId)
        : [...store.selectedFoldersIdsModal(), folderId];
      patchState(store, { selectedFoldersIdsModal });
    },
    setSelectedFoldersIdsModal: (selectedFoldersIdsModal: string[]) => patchState(store, { selectedFoldersIdsModal }),
    setContentViewType: (contentViewType: IrisContentViewType = IrisContentViewType.List) => patchState(store, { contentViewType }),
    setOpenedMenuFolderId: (openedMenuFolderId: string) => patchState(store, { openedMenuFolderId, openedMenuFileId: null }),
    setOpenedMenuFileId: (openedMenuFileId: string) => patchState(store, { openedMenuFileId, openedMenuFolderId: null }),
    addUploadingFiles: (uploadingFiles: IrisDMSUploadingFile[]) => {
      const filesToAdd = differenceBy(uploadingFiles, store.uploadingFiles(), 'name');
      patchState(store, { uploadingFiles: [...store.uploadingFiles(), ...filesToAdd] });
    },
    updateUploadingFileState: (name: string, newState: FileUploadState) => {
      const uploadingFiles = [...store.uploadingFiles()];
      const fileIndex = uploadingFiles.findIndex(file => file.name === name);
      if (fileIndex < 0) {
        return;
      }
      const file = uploadingFiles[fileIndex];
      const fileState = newState === FileUploadState.Completed && file.state === FileUploadState.Canceled ? file.state : newState;
      uploadingFiles.splice(fileIndex, 1, { ...file, state: fileState });
      patchState(store, { uploadingFiles });
    },
    removeUploadingFile: (name: string) =>
      patchState(store, { uploadingFiles: store.uploadingFiles().filter(file => file.name !== name) }),
    resetUploadingFiles: () => patchState(store, { uploadingFiles: [] }),
    showUploadMinimizedModal: (uploadMinimizedModalShown: boolean) => patchState(store, { uploadMinimizedModalShown }),
    addImportingFolderTree: (importingFolderTree: ImportingFolderTreeStatus) =>
      patchState(store, { importingFolderTrees: [...store.importingFolderTrees(), importingFolderTree] }),
    updateImportingFolderTree: (importingFolderTree: ImportingFolderTreeStatus) => {
      const importingFolderTrees = [...store.importingFolderTrees()];
      const itemIndex = importingFolderTrees.findIndex(({ name }) => name === importingFolderTree.name);
      if (itemIndex < 0) {
        return;
      }
      importingFolderTrees.splice(itemIndex, 1, importingFolderTree);
      patchState(store, { importingFolderTrees });
    },
    removeImportingFolderTree: (folderName: string) =>
      patchState(store, { importingFolderTrees: store.importingFolderTrees().filter(({ name }) => name !== folderName) }),
    resetImportingFolderTrees: () => patchState(store, { importingFolderTrees: [] }),
    setUploadedFilesIds: (files: IrisDMSFileI[]) => {
      const uploadingFiles = store.uploadingFiles().map(file => {
        const uploadedFile = files.find(({ name }) => file.name === name);
        return uploadedFile ? { ...file, id: uploadedFile.id, folderId: uploadedFile.parentId } : file;
      });
      patchState(store, { uploadingFiles });
    },
    addUploadingFileFolder: (name: string, folderId: string) => {
      const uploadingFiles = store.uploadingFiles().map(file => file.name === name ? { ...file, folderId } : file);
      patchState(store, { uploadingFiles });
    },
    setCheckAll: (checkAll: boolean) => patchState(store, { checkAll }),
    toggleFoldersTreeVisibility: (force?: boolean) => {
      const showFoldersTree = isBoolean(force) ? force : !store.showFoldersTree();
      patchState(store, { showFoldersTree });
    },
  })),
  withMethods((
    store,
    deeplTranslationService = inject(IrisDeeplTranslationService),
    envService = inject(IrisEnvironmentService),
    dmsSidebarStore = inject(IrisDmsSidebarStore),
  ) => ({
    resetSelectedFolderId: () => store.folderSelected(null),
    resetSelectedFoldersIdsModal: () => store.setSelectedFoldersIdsModal([]),
    fetchDeeplLanguages$: rxMethod<void>(
      pipe(
        switchMap(() => deeplTranslationService.getDeeplSupportedLanguages(envService.env.instanceId).pipe(
          tapResponse({
            next: deeplLanguages => patchState(store, { deeplLanguages }),
            error: () => patchState(store, { deeplLanguages: [] }),
          }),
        )),
      ),
    ),
    setFolderViewSelectedTab: (selectedTab: IrisDmsFolderViewTab) => {
      dmsSidebarStore.hideSidebar();
      const folderViewSelectedTab = !store.isAdmin() && selectedTab === IrisDmsFolderViewTab.SystemFolders
        ? selectedTab + 1
        : selectedTab;
      patchState(store, { folderViewSelectedTab });
    },
  })),
  withMethods((store) => ({
    selectDocumentsTab: () => store.setFolderViewSelectedTab(IrisDmsFolderViewTab.Documents),
    selectTrashTab: () => store.setFolderViewSelectedTab(IrisDmsFolderViewTab.Trash),
    selectTagsTab: () => store.setFolderViewSelectedTab(IrisDmsFolderViewTab.Tags),
    selectSystemFoldersTab: () => store.setFolderViewSelectedTab(IrisDmsFolderViewTab.SystemFolders),
  })),
  withHooks({
    onInit: (
      store,
      infrastructureService = inject(IrisInfrastructureService),
    ) => {
      store.toggleFoldersTreeVisibility(!infrastructureService.isTablet);
    },
  }),
);
